import {isEmpty} from 'lodash'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {SortType} from 'rsuite-table/lib/@types/common'
import IconButton from 'rsuite/IconButton'
import Pagination from 'rsuite/Pagination'
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'

import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'

import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as tisaForexRateCategoryRedux from 'src/app/modules/rates/tisaForexRateCategory/redux/index'
import {ITisaForexRateCategoryState} from 'src/app/modules/rates/tisaForexRateCategory/redux/reducer'
import AddTisaForexRateCategory from './AddTisaForexRateCategory'

const Cell = Table.Cell

const TisaForexRateCategoryComponent = () => {
  const dispatch = useDispatch()

  const [open, setOpen] = useState(false)
  const [actionType, setActionType] = useState('Add')
  const [editSelectedData, setEditSelectedData] = useState<any>({})
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])
  const [alertOpen, setAlertOpen] = useState(false)

  const [params, setParams] = useState<StateParamsModel>({
    page: 1,
    limit: 10,
  })

  const tisaForexRateCategoryData: ITisaForexRateCategoryState = useSelector(
    (state: any) => state.tisaForexRateCategory
  )

  const {activateSuccess, deactivateSuccess, deleteSuccess, success, loading} =
    tisaForexRateCategoryData

  useEffect(() => {
    dispatch(tisaForexRateCategoryRedux.actions.getAllTisaForexRateCategory(params))
  }, [params])

  useEffect(() => {
    if (activateSuccess) {
      toast.success('Tisa Forex Rate Category activated successfully')
    }
    if (deactivateSuccess) {
      toast.success('Tisa Forex Rate Category deactivated successfully')
    }
    handleChecked([])
    dispatch(tisaForexRateCategoryRedux.actions.getAllTisaForexRateCategory(params))
  }, [activateSuccess, deactivateSuccess])

  useEffect(() => {
    if (deleteSuccess) {
      toast.success('Tisa Forex Rate Category deleted successfully')
      dispatch(tisaForexRateCategoryRedux.actions.getAllTisaForexRateCategory(params))
    }
  }, [deleteSuccess])

  const handleToggleAction = (rowData: {[key: string]: any}) => {
    if (rowData?.status) {
      dispatch(
        tisaForexRateCategoryRedux.actions.singleDeactivateTisaForexRateCategory([
          {id: rowData.id},
        ])
      )
    } else {
      dispatch(
        tisaForexRateCategoryRedux.actions.singleActivateTisaForexRateCategory([{id: rowData.id}])
      )
    }
  }

  const ActionCell = ({rowData, dataKey, ...props}: any) => (
    <Cell {...props} className='link-group'>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Edit</Tooltip>}>
          <IconButton
            appearance='subtle'
            onClick={() => {
              setEditSelectedData(rowData)
              setActionType('Edit')
              setOpen(true)
            }}
            icon={<Edit2 />}
            data-cy='edit-button'
          />
        </Whisper>
      </CheckPermissions>

      <CheckPermissions type='Delete'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Delete</Tooltip>}>
          <IconButton
            appearance='subtle'
            onClick={() => {
              setCheckedValues([rowData.id])
              setAlertOpen(true)
            }}
            icon={<TrashIcon />}
            data-cy='delete-button'
          />
        </Whisper>
      </CheckPermissions>

      <Whisper placement='top' trigger='hover' speaker={<Tooltip>Status</Tooltip>}>
        <Toggle
          size='sm'
          checked={rowData.status}
          onClick={() => handleToggleAction(rowData)}
          data-cy='toggle-button'
        />
      </Whisper>
    </Cell>
  )

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 60,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Name',
      dataKey: 'name',
      flexGrow: 1,
      cell: <Cell dataKey='name' />,
      sortable: true,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 85,
      cell: (
        <Cell>
          {(rowData) =>
            rowData?.status ? (
              <Tag color='green' size='sm'>
                Active
              </Tag>
            ) : (
              <Tag color='red' size='sm'>
                Inactive
              </Tag>
            )
          }
        </Cell>
      ),
      sortable: false,
    },
    {
      label: 'Action',
      width: 150,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
    },
  ]

  const data = tisaForexRateCategoryData?.allData
    ? tisaForexRateCategoryData.allData.map((item: any, i: number) => ({
        sn: i + 1,
        id: item?.id,
        name: item?.name,
        code: item?.code,
        order: item?.order,
        currency_unit: item?.currency_unit,
        status: item?.is_enabled,
      }))
    : []

  const handleChecked = (values: Array<string>) => setCheckedValues(values)

  const handleSort = (dataKey: string, sortType?: SortType) =>
    setParams({...params, orderBy: dataKey, order: sortType})

  const handleChangeLimit = (dataKey: number) => setParams({...params, limit: dataKey})

  const handleAddModal = () => {
    setEditSelectedData({})
    setActionType('Add')
    setOpen(true)
  }

  const handleRefresh = () =>
    dispatch(tisaForexRateCategoryRedux.actions.getAllTisaForexRateCategory(params))

  const handleDelete = () => {
    const ids = checkedValues.map((id) => ({id}))
    dispatch(tisaForexRateCategoryRedux.actions.deleteTisaForexRateCategory(ids))
    setAlertOpen(false)
  }

  return (
    <div className='shadow p-3 bg-white rounded'>
      <DesignComponent
        moduleName='Tisa Forex Rate Category'
        params={params}
        setParams={setParams}
        handleRefresh={handleRefresh}
        handleAddModal={handleAddModal}
        handleAlertOpen={() => setAlertOpen(true)}
        exportShow={false}
        importShow={false}
        enableMultiple={() => {
          if (!isEmpty(checkedValues)) {
            dispatch(
              tisaForexRateCategoryRedux.actions.singleActivateTisaForexRateCategory(
                checkedValues.map((id) => ({id}))
              )
            )
          } else {
            toast.error('No data selected')
          }
        }}
        disableMultiple={() => {
          if (!isEmpty(checkedValues)) {
            dispatch(
              tisaForexRateCategoryRedux.actions.singleDeactivateTisaForexRateCategory(
                checkedValues.map((id) => ({id}))
              )
            )
          } else {
            toast.error('No data selected')
          }
        }}
      />

      <div className='datatable'>
        <RSuiteTable
          showCheckbox={true}
          columns={columns}
          data={data}
          checkedValues={checkedValues}
          showLoading={loading}
          onChecked={handleChecked}
          handleSort={handleSort}
        />
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          className='mt-5'
          maxButtons={5}
          size='sm'
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={tisaForexRateCategoryData?.allMeta?.total || 0}
          limitOptions={[10, 20, 50, 100]}
          limit={params.limit}
          activePage={params.page}
          onChangePage={(value) => setParams({...params, page: value})}
          onChangeLimit={handleChangeLimit}
        />
      </div>

      {open && (
        <AddTisaForexRateCategory
          open={open}
          handleClose={() => setOpen(false)}
          actionType={actionType}
          editSelectedData={editSelectedData}
        />
      )}

      {alertOpen &&
        (!isEmpty(checkedValues) ? (
          <DeleteModal
            handleClick={handleDelete}
            isOpen={alertOpen}
            handleClose={() => setAlertOpen(false)}
          />
        ) : (
          toast.error('No data selected') && setAlertOpen(false)
        ))}
    </div>
  )
}

export default TisaForexRateCategoryComponent
